import { createSlice } from '@reduxjs/toolkit';
import { QuestionnaireRequestAnswerRequest } from '../../../../api/generated/prometeia';
import { updateQuestionnairesStatus } from '../home-thunks';
import {
  getMifidImage,
  getMifidPfSurvey,
  getMifidPfThematicSurvey,
  writeMifidPfSurvey,
  writeMifidNoProfiling,
} from './home-onboarding-self-mifid-survey-thunks';
import { MifidSurveyState } from './home-onboarding-self-mifid-survey-types';
const initialState: MifidSurveyState = {
  getMifidImageError: false,
  getMifidImageStatus: 'idle',
  writeMifidError: false,
  writeMifidStatus: 'idle',
  registerMifidStatus: 'idle',
  mifidImages: [],
  getMifidStatus: 'idle',
  getMifidError: false,
  survey: undefined,
  surveyTemplate: undefined,
  partialCustomerQuestionnaireAnswers: [],
  isMifidCurrentPage: false,
  quitMifid: null,
  categorisationRight: '',
  formCode: '',
  instituteCode: '',
  thematicAreaList: [],
  userId: '',
  updateQuestionnairesStatusError: false,
  updateQuestionnairesStatusStatus: 'idle',
  writeMifidResponse: undefined,
  surveyStatus: [],
};
const mifidSurveySlice = createSlice({
  name: 'features/homeOnboardingSelfMifidSurvey',
  initialState,
  reducers: {
    resetSurvey(state) {
      state.survey = undefined;
      state.getMifidStatus = 'idle';
      state.getMifidError = false;
      state.writeMifidStatus = 'idle';
      state.writeMifidError = false;
      state.updateQuestionnairesStatusStatus = 'idle';
      state.updateQuestionnairesStatusError = false;
      state.writeMifidResponse = undefined;
      state.surveyStatus = [];
    },
    resetUpdateQuestionnairesStatus(state) {
      state.updateQuestionnairesStatusStatus = 'idle';
      state.updateQuestionnairesStatusError = false;
    },
    resetPartialQuestionnaireAnswers(state) {
      state.partialCustomerQuestionnaireAnswers = [];
    },
    updatePartialQuestionnaireAnswers(state, action) {
      state.partialCustomerQuestionnaireAnswers = action.payload;
    },
    setIsMifidCurrentPage(state, action) {
      state.isMifidCurrentPage = action.payload;
    },
    setQuitMifid(state, action) {
      state.quitMifid = action.payload;
    },
    addAnswers(state, action) {
      let answers = state.partialCustomerQuestionnaireAnswers;
      answers = answers.filter(
        (answer) => answer.questionCode !== action.payload[0].questionCode
      );
      action.payload.forEach((answer) => {
        answers.push(answer);
      });
      state.partialCustomerQuestionnaireAnswers = answers;
    },
    removeAnswers(state, action) {
      state.partialCustomerQuestionnaireAnswers =
        state.partialCustomerQuestionnaireAnswers.filter(
          (answer) => answer.questionCode !== action.payload
        );
    },
    updateSurveyStatusMobile(state, action) {
      let status = state.surveyStatus;
      status = status.filter((s) => !action.payload.startsWith(s.questionId));
      state.surveyStatus = status;
    },
    updateSurveyStatus(state, action) {
      state.surveyStatus = action.payload;
    },
    resetSurveyStatus(state) {
      state.surveyStatus = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMifidPfSurvey.pending, (state) => {
      state.getMifidStatus = 'loading';
      state.getMifidError = false;
    });
    builder.addCase(getMifidPfSurvey.fulfilled, (state, action) => {
      state.getMifidStatus = 'succeeded';
      state.survey = action.payload;
      state.getMifidError = false;
      state.partialCustomerQuestionnaireAnswers =
        action.payload?.profiloRischio?.answerList?.map(
          (a) => a as QuestionnaireRequestAnswerRequest
        ) || [];
      state.categorisationRight =
        action.payload?.profiloRischio?.categorisationRight;
      state.formCode = action.payload?.formCode;
      state.instituteCode = action.payload?.profiloRischio?.instituteCode;
      state.thematicAreaList = action.payload?.strutturaQuestionario?.map(
        (e) => {
          return e.thematicAreaList[0]?.thematicAreaCode;
        }
      );
      state.userId = action.payload?.userId;
    });
    builder.addCase(getMifidPfSurvey.rejected, (state) => {
      state.getMifidStatus = 'failed';
      state.getMifidError = true;
    });
    builder.addCase(getMifidPfThematicSurvey.pending, (state) => {
      state.getMifidStatus = 'loading';
      state.getMifidError = false;
    });
    builder.addCase(getMifidPfThematicSurvey.fulfilled, (state, action) => {
      state.getMifidStatus = 'succeeded';
      state.surveyTemplate = action.payload;
      state.getMifidError = false;
    });
    builder.addCase(getMifidPfThematicSurvey.rejected, (state) => {
      state.getMifidStatus = 'failed';
      state.getMifidError = true;
    });
    builder.addCase(writeMifidPfSurvey.pending, (state) => {
      state.writeMifidStatus = 'loading';
      state.writeMifidError = false;
    });
    builder.addCase(writeMifidPfSurvey.fulfilled, (state, action) => {
      state.writeMifidStatus = 'succeeded';
      state.writeMifidError = false;
      state.writeMifidResponse = action.payload;
    });
    builder.addCase(writeMifidPfSurvey.rejected, (state) => {
      state.writeMifidStatus = 'failed';
      state.writeMifidError = true;
    });
    builder.addCase(writeMifidNoProfiling.pending, (state) => {
      state.writeMifidStatus = 'loading';
      state.writeMifidError = false;
    });
    builder.addCase(writeMifidNoProfiling.fulfilled, (state) => {
      state.writeMifidStatus = 'succeeded';
      state.writeMifidError = false;
      state.writeMifidResponse = { incoeherenceList: [] };
    });
    builder.addCase(writeMifidNoProfiling.rejected, (state) => {
      state.writeMifidStatus = 'failed';
      state.writeMifidError = true;
    });
    builder.addCase(getMifidImage.pending, (state) => {
      state.getMifidImageStatus = 'loading';
      state.getMifidImageError = false;
    });
    builder.addCase(getMifidImage.fulfilled, (state, action) => {
      state.getMifidImageStatus = 'succeeded';
      state.getMifidImageError = false;
      state.mifidImages = state.mifidImages.concat([action.payload]);
    });
    builder.addCase(getMifidImage.rejected, (state) => {
      state.getMifidImageStatus = 'failed';
      state.getMifidImageError = true;
    });
    builder.addCase(updateQuestionnairesStatus.pending, (state) => {
      state.updateQuestionnairesStatusStatus = 'loading';
      state.updateQuestionnairesStatusError = false;
    });
    builder.addCase(updateQuestionnairesStatus.fulfilled, (state) => {
      state.updateQuestionnairesStatusStatus = 'succeeded';
      state.updateQuestionnairesStatusError = false;
    });
    builder.addCase(updateQuestionnairesStatus.rejected, (state) => {
      state.updateQuestionnairesStatusStatus = 'failed';
      state.updateQuestionnairesStatusError = true;
    });
  },
});
export const {
  resetPartialQuestionnaireAnswers,
  updatePartialQuestionnaireAnswers,
  addAnswers,
  removeAnswers,
  resetSurvey,
  setIsMifidCurrentPage,
  setQuitMifid,
  updateSurveyStatus,
  resetSurveyStatus,
  updateSurveyStatusMobile,
  resetUpdateQuestionnairesStatus,
} = mifidSurveySlice.actions;
export default mifidSurveySlice.reducer;
